// @flow

import React, { Component, type Node } from 'react';
import { connect } from 'react-redux';
import changeWindowLocation from 'app/common/lib/util/changeWindowLocation';
import ga from 'app/common/lib/ga';
import getClientTrackingParams from 'app/packages/client-tracking-params';
import openExternalLinkFromPWA from 'app/common/react/components/Windows/openExternalLinkFromPWA';
import PropTypes from 'prop-types';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import SafeLink from 'app/common/lib/SafeLink';
import type { Router as RouterType } from 'app/common/lib/ReactRouter/types';
import urlUtil from 'app/common/lib/urlUtil';
import withExperienceAnchor from 'app/common/lib/react/experiences/withExperienceAnchor';
import { compose } from 'redux';
import { dispatchShowSpammyClickthroughWarning } from 'app/common/react/ui/SpammyClickthroughWarningModal/dispatchSpammyClickthroughWarningActions';
import { withRouter } from 'react-router';
import type { State as LocationState, Entry } from 'app/common/redux/reducers/location';

// TODO(mjennings)- merge with import type { Pin } from 'app/common/react/components/closeup/types';
type Pin = {
  board?: {
    url: string,
  },
  id: string,
  pinner?: {
    username: string,
  },
  tracking_params: string,
  tracking_params_map: Object,
};

type Location = {
  history: Array<Entry>,
  pathname: string,
};

const createOffsiteLinkResource = (
  pin: Pin,
  location: Location,
  url: string,
  check_only: boolean,
  auxData?: Object,
) => {
  const kwargs = {
    check_only,
    client_tracking_params: getClientTrackingParams(pin, location),
    pin_id: pin.id,
    url,
    aux_data: JSON.stringify(auxData),
  };
  return ResourceFactory.create('OffsiteLinkResource', kwargs);
};

type ReduxProps = {|
  location: LocationState,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  pin?: *,
|};

type OwnProps = {|
  auxData?: Object,
  category?: string,
  children?: Node,
  className?: string,
  crsId?: string,
  deeplinkUrls?: {
    androidDeeplink: string,
    iOSDeeplink: string,
    webUrl?: string,
  },
  elementType?: number,
  external?: boolean,
  extraData?: {},
  focusOnBody?: boolean,
  forceOnClick?: boolean,
  forceRefresh?: number,
  gaCategory?: string,
  internalNewTab?: boolean,
  noFollow?: boolean,
  onClick?: (SyntheticEvent<>) => void,
  pinId?: string,
  router?: RouterType,
  shouldCaptureClick?: boolean,
  shouldShowAltLinks?: boolean,
  stopPropagation?: boolean,
  style?: {},
  target?: boolean,
  to: string,
  useNewTab?: boolean,
|};

type Props = {|
  ...OwnProps,
  ...ReduxProps,
|};

type State = {|
  spamCheck: ?{|
    blocked: boolean,
    message: string,
    redirectStatus: string,
    sanitized_url: string,
  |},
|};

export class NavigateLink extends Component<Props, State> {
  state = { spamCheck: null };

  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  componentDidUpdate() {
    // Copied/Adapted from the deleted ExperimentalNavigateLink component
    // https://phabricator.pinadmin.com/D452496
    const { external: externalProp, location, pin, to: url } = this.props;
    const { spamCheck } = this.state;

    const shouldCheckIfSpam =
      (externalProp || urlUtil.isOffsiteUrl(url)) && // for parity with Denzel
      pin &&
      location.pathname.includes(pin.id) &&
      url &&
      spamCheck === null;

    if (shouldCheckIfSpam) {
      createOffsiteLinkResource(pin, location, url, true)
        .callGet({ showError: false })
        .then((response) => {
          if (response && response.resource_response && response.resource_response.error) {
            // fall back to old handling of offsite - will be slower
            return;
          }

          if (this.mounted) {
            const { message, redirect_status, url: sanitized_url } =
              response && response.resource_response && response.resource_response.data;

            const blocked = ['blocked', 'suspicious', 'porn'].includes(redirect_status);

            this.setState({
              spamCheck: { blocked, message, redirectStatus: redirect_status, sanitized_url },
            });
          }
        });
    }
  }

  mounted = false;

  /**
   * Set the focus on the body element after clicking a link
   * We do this for a11y & visual reasons
   * More info at https://stackoverflow.com/a/6976583/117193
   */
  setFocusOnBody = () => {
    if (window) {
      window.focus();
    }
    if (document.activeElement) {
      document.activeElement.blur();
    }
  };

  handleClick = (e: SyntheticMouseEvent<>) => {
    const {
      auxData,
      external: externalProp,
      focusOnBody,
      forceOnClick,
      gaCategory,
      location,
      internalNewTab,
      onClick,
      pin,
      pinId,
      router: reactRouter,
      shouldShowAltLinks,
      stopPropagation: shouldStopPropagation,
      to: url,
    } = this.props;

    const { spamCheck } = this.state;

    if (gaCategory) {
      ga.trackEvent(gaCategory, PCONST.GA_ACTION_CLICK);
    }

    if (shouldStopPropagation) {
      e.stopPropagation();
    }

    const external = externalProp || urlUtil.isOffsiteUrl(url); // for parity with Denzel

    // Allow native command/control+click open in new tab for non-external links.
    if (!external && (e.metaKey || e.ctrlKey) && !forceOnClick) {
      // Note that:
      // 1. We are NOT triggering the click handler when user opens an internal link
      // in a new tab because the click handler may not take this case into account,
      // and does something with the false assumption that the user is navigating away.
      // The tradeoff here is that we may be missing logging for such events
      // TODO(jackhsu): figure out the best way to allow logging for such events.
      //
      // 2. We are ignoring shiftKey, altKey, and middle click for consistency w/ Denzel.
      // Ideally, we should allow them to be handled by the browser as well.
      return;
    }

    e.nativeEvent.preventDefault();

    if (onClick) {
      onClick(e);
    }

    // TODO(imad): reimplement signup modal functionality.
    // will keep this code snippet with TODO for fast reference.
    // if (url && url.indexOf('/login') !== -1 &&
    //    getInitModuleName() !== 'home_page' &&
    //    getInitModuleName() !== 'login_page' &&
    //    getInitModuleName() !== 'landing_page_analytics') {
    //    return signupModalManager.showInlineSignupModalOnButtonClick('LoginButton');
    // }

    if (!url || shouldShowAltLinks) {
      return;
    }

    // Using feature detection here
    if (external && typeof window !== 'undefined' && window.Windows) {
      // NOTE: Opening external links on Windows PWA need special
      // care if we want it to be opened in the user's browser.
      openExternalLinkFromPWA({ url, pinId, pin, location, auxData });
    } else if (external && pin) {
      if (spamCheck && spamCheck.blocked) {
        dispatchShowSpammyClickthroughWarning(spamCheck);
      } else {
        SafeLink.open(url, pinId, null, getClientTrackingParams(pin, location), auxData);
      }
    } else if (external) {
      SafeLink.open(url, pinId);
    } else if (urlUtil.getUrlClass(url) === urlUtil.UrlClass.TRUSTED_DIFFERENT_ORIGIN) {
      changeWindowLocation(url, false);
    } else if (internalNewTab) {
      // Navigate to internal Pinterest page in new tab
      changeWindowLocation(url, true);
    } else if (reactRouter) {
      reactRouter.push(url);
      if (focusOnBody) {
        this.setFocusOnBody();
      }
    }
  };

  render() {
    const {
      category,
      children,
      className,
      elementType,
      external,
      forceRefresh,
      noFollow,
      shouldCaptureClick,
      style,
      target,
      to,
    } = this.props;

    const linkProps = {
      className,
      'data-category': category,
      'data-force-refresh': forceRefresh,
      href: to,
      [shouldCaptureClick ? 'onClickCapture' : 'onClick']: this.handleClick,
      rel: external || noFollow ? 'nofollow' : '',
      style,
      target: target ? '_blank' : null,
      ...(elementType !== null ? { 'data-element-type': elementType } : {}),
    };

    return <a {...linkProps}>{children}</a>;
  }
}

NavigateLink.propTypes = {
  shouldShowAltLinks: PropTypes.bool,
  category: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  crsId: PropTypes.string,
  deeplinkUrls: PropTypes.shape({
    androidDeeplink: PropTypes.string.isRequired,
    iOSDeeplink: PropTypes.string.isRequired,
    webUrl: PropTypes.string,
  }),
  elementType: PropTypes.number,
  external: PropTypes.bool,
  extraData: PropTypes.shape({}),
  focusOnBody: PropTypes.bool,
  forceOnClick: PropTypes.bool,
  forceRefresh: PropTypes.number,
  gaCategory: PropTypes.string,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    history: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  internalNewTab: PropTypes.bool,
  noFollow: PropTypes.bool,
  onClick: PropTypes.func,
  pin: PropTypes.shape({
    id: PropTypes.string,
    board: PropTypes.shape({
      url: PropTypes.string.isRequired,
    }),
    pinner: PropTypes.shape({
      username: PropTypes.string.isRequired,
    }),
    tracking_params: PropTypes.string,
    tracking_params_map: PropTypes.shape({}),
  }),
  pinId: PropTypes.string,
  router: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
  stopPropagation: PropTypes.bool,
  style: PropTypes.shape({}),
  to: PropTypes.string,
  useNewTab: PropTypes.bool,
};

NavigateLink.contextTypes = {
  country: PropTypes.string,
  isAuthenticated: PropTypes.bool,
  unauthId: PropTypes.string,
  userAgent: PropTypes.shape({
    canUseNativeApp: PropTypes.bool.isRequired,
    isTablet: PropTypes.bool.isRequired,
    platform: PropTypes.string.isRequired,
    platformVersion: PropTypes.arrayOf(PropTypes.number),
    raw: PropTypes.string.isRequired,
  }),
};

export const NavigateLinkHome = compose(
  withExperienceAnchor('.HomeButton2'),
  withRouter,
)(NavigateLink);

export const NAV_MAP = {
  '.HomeButton2': NavigateLinkHome,
};

export default compose(
  withRouter,
  connect(
    ({ location, pins }, { pinId }): ReduxProps => ({
      location,
      pin: pinId && pins && pins[pinId],
    }),
  ),
)(NavigateLink);
